import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { userRequest } from '../utils/requestMethods';
import { Header, CustomPagination, SkeletonTable, BackButton, StudentClockInHistoryModal, ClockInOutSettingsModal } from '../components';
import { useStateContext } from '../contexts/ContextProvider';
import { toast } from 'react-toastify';
import { MdSearch } from 'react-icons/md';
import { FiClock, FiLogIn, FiLogOut, FiUsers, FiAlertCircle, FiCalendar, FiDownload, FiSettings } from 'react-icons/fi';
import * as Icons from '../data/class';
import { format, parseISO } from 'date-fns';

const customStyles = {
  headRow: {
    style: {
      backgroundColor: '#f8fafc',
      fontWeight: "600",
      fontSize: '14px',
      color: '#475569',
      borderBottom: '2px solid #e2e8f0',
    },
  },
  headCells: {
    style: {
      paddingLeft: '20px',
      paddingRight: '20px',
      paddingTop: '16px',
      paddingBottom: '16px',
    },
  },
  cells: {
    style: {
      paddingLeft: '20px',
      paddingRight: '20px',
      paddingTop: '12px',
      paddingBottom: '12px',
    },
  },
  rows: {
    style: {
      borderBottom: '1px solid #f1f5f9',
      '&:hover': {
        backgroundColor: '#f8fafc',
      },
    },
  },
};

const ClockInOutHistory = ({ history }) => {
  const { school, refreshSchool } = useStateContext();
  const schoolId = school?._id;

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showClassDropdown, setShowClassDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  
  // Modal state
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  // Get current term dates from school settings
  const currentTerm = school?.settings?.current_term;
  const currentTermData = school?.settings?.terms?.find(term => term.term === currentTerm);
  const termStartDate = currentTermData?.start_date ? format(parseISO(currentTermData.start_date), 'yyyy-MM-dd') : null;
  const termEndDate = currentTermData?.end_date ? format(parseISO(currentTermData.end_date), 'yyyy-MM-dd') : null;
  
  // Date filters - default to today
  const today = new Date();
  const [fromDate, setFromDate] = useState(format(today, 'yyyy-MM-dd'));
  const [toDate, setToDate] = useState(format(today, 'yyyy-MM-dd'));

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);

  // Statistics
  const [stats, setStats] = useState({
    totalClockedIn: 0,
    totalClockedOut: 0,
    currentlyInSchool: 0,
    totalEntries: 0,
  });

  useEffect(() => {
    if (schoolId) {
      fetchClasses();
      fetchClockInHistory();
      fetchClockInStats();
    }
  }, [schoolId, selectedClass, selectedStatus, fromDate, toDate, page]);

  const fetchClasses = async () => {
    try {
      const { data } = await userRequest.get(`classes?code=${school?.code}`);
      setClasses(data.classes || []);
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const buildFilterParams = () => {
    const params = new URLSearchParams({ from: fromDate, to: toDate });
    if (selectedClass) params.append('class_id', selectedClass);
    if (selectedStatus !== 'all') params.append('status', selectedStatus);
    if (searchText) params.append('search', searchText);
    return params;
  };

  const fetchClockInHistory = async () => {
    try {
      setLoading(true);
      const params = buildFilterParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      params.append('order', 'desc');
      params.append('sort_by', 'date');

      const { data } = await userRequest.get(`/children/${schoolId}/clock-in-history?${params}`);
      setRecords(data.records || []);
      setTotalRecords(data.total_documents || 0);
    } catch (error) {
      console.error('Error fetching clock-in history:', error);
      toast.error('Failed to load clock-in history');
    } finally {
      setLoading(false);
    }
  };

  const fetchClockInStats = async () => {
    try {
      const params = buildFilterParams();
      const { data } = await userRequest.get(`/children/${schoolId}/clock-in-stats?${params}`);
      setStats({
        totalClockedIn: data.totalClockIns ?? 0,
        totalClockedOut: data.totalClockOuts ?? 0,
        currentlyInSchool: data.currentlyInSchool ?? 0,
        totalEntries: data.totalEntries ?? 0,
      });
    } catch (error) {
      console.error('Error fetching clock-in stats:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchClockInHistory();
    fetchClockInStats();
  };

  const handleExport = () => {
    // Export functionality - would generate CSV/PDF
    toast.info('Export functionality coming soon');
  };

  const handleStudentClick = (student) => {
    setSelectedStudent(student);
    setShowStudentModal(true);
  };

  const handleCloseStudentModal = () => {
    setShowStudentModal(false);
    setSelectedStudent(null);
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      return format(parseISO(timestamp), 'hh:mm a');
    } catch {
      return 'Invalid time';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      clocked_in: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        label: 'In School',
        icon: <FiLogIn className="w-3 h-3" />
      },
      clocked_out: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        label: 'Left School',
        icon: <FiLogOut className="w-3 h-3" />
      },
    };

    const badge = badges[status] || badges.clocked_in;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const columns = [
    {
      name: "Student",
      cell: row => (
        <div className="py-2">
          <button
            onClick={() => handleStudentClick(row)}
            className="text-left hover:bg-blue-50 rounded-lg p-2 -m-2 transition-colors"
          >
            <div className="font-semibold text-blue-600 hover:text-blue-700 text-sm">
              {row.student_name}
            </div>
            <div className="text-xs text-slate-500">
              {row.class_name}
            </div>
          </button>
        </div>
      ),
      sortable: true,
      minWidth: "200px",
    },
    {
      name: "Date",
      selector: row => (
        <div className="py-2">
          <div className="text-sm text-slate-700">
            {formatDate(row.date)}
          </div>
          <div className="text-xs text-slate-500">
            Week {row.week}, Day {row.day}
          </div>
        </div>
      ),
      sortable: true,
      minWidth: "140px",
    },
    {
      name: "Clock In",
      selector: row => (
        <div className="py-2">
          <div className="flex items-center gap-2 text-sm text-green-700 font-medium">
            <FiLogIn className="w-4 h-4" />
            {formatTime(row.clock_in?.timestamp)}
          </div>
          {row.clock_in?.marked_by && (
            <div className="text-xs text-slate-500 mt-1">
              by {row.clock_in.marked_by.name}
            </div>
          )}
        </div>
      ),
      minWidth: "160px",
    },
    {
      name: "Clock Out",
      selector: row => (
        <div className="py-2">
          {row.clock_out ? (
            <>
              <div className="flex items-center gap-2 text-sm text-gray-700 font-medium">
                <FiLogOut className="w-4 h-4" />
                {formatTime(row.clock_out?.timestamp)}
              </div>
              {row.clock_out?.marked_by && (
                <div className="text-xs text-slate-500 mt-1">
                  by {row.clock_out.marked_by.name}
                </div>
              )}
            </>
          ) : (
            <div className="text-xs text-slate-400">Not clocked out</div>
          )}
        </div>
      ),
      minWidth: "160px",
    },
    {
      name: "Duration",
      selector: row => {
        if (!row.clock_in || !row.clock_out) {
          return <span className="text-xs text-slate-400">-</span>;
        }
        
        const clockIn = new Date(row.clock_in.timestamp);
        const clockOut = new Date(row.clock_out.timestamp);
        const diffMs = clockOut - clockIn;
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        
        return (
          <div className="py-2">
            <div className="flex items-center gap-1 text-sm text-slate-700">
              <FiClock className="w-4 h-4 text-blue-600" />
              <span className="font-medium">{hours}h {minutes}m</span>
            </div>
          </div>
        );
      },
      minWidth: "120px",
    },
    {
      name: "Status",
      selector: row => (
        <div className="py-2">
          {getStatusBadge(row.status)}
        </div>
      ),
      sortable: true,
      minWidth: "120px",
    },
  ];

  const filteredRecords = records.filter(record =>
    record.student_name?.toLowerCase().includes(searchText.toLowerCase())
  );

  const getSelectedClassName = () => {
    if (!selectedClass) return 'All Classes';
    const selected = classes.find(c => c._id === selectedClass);
    return selected?.class_name || 'All Classes';
  };

  const getSelectedStatusLabel = () => {
    const labels = {
      all: 'All Status',
      clocked_in: 'In School',
      clocked_out: 'Left School',
    };
    return labels[selectedStatus] || 'All Status';
  };

  return (
    <>
      <div className="px-4 md:px-10 pt-4 md:pt-6 mt-20 md:mt-0">
        <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
          <div className="flex-1 min-w-0">
            <Header category="Student Management" title="Gate Monitoring" />
          </div>
          <button
            onClick={() => setShowSettingsModal(true)}
            className="inline-flex items-center px-3 md:px-4 py-2 md:py-2.5 bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-700 font-medium rounded-full transition-colors shadow-sm flex-shrink-0"
          >
            <FiSettings className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Settings</span>
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="px-3 md:px-10 pt-2 md:pt-5">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-8">
          <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-xs md:text-sm mb-1">In School</p>
                <p className="text-2xl md:text-3xl font-bold text-green-600">{stats.currentlyInSchool}</p>
              </div>
              <div className="w-10 h-10 md:w-14 md:h-14 bg-green-100 rounded-xl flex items-center justify-center">
                <FiUsers className="w-5 h-5 md:w-7 md:h-7 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-xs md:text-sm mb-1">Clock Ins</p>
                <p className="text-2xl md:text-3xl font-bold text-blue-600">{stats.totalClockedIn}</p>
              </div>
              <div className="w-10 h-10 md:w-14 md:h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                <FiLogIn className="w-5 h-5 md:w-7 md:h-7 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-xs md:text-sm mb-1">Clock Outs</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-600">{stats.totalClockedOut}</p>
              </div>
              <div className="w-10 h-10 md:w-14 md:h-14 bg-gray-100 rounded-xl flex items-center justify-center">
                <FiLogOut className="w-5 h-5 md:w-7 md:h-7 text-gray-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4 md:p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-xs md:text-sm mb-1">Total Entries</p>
                <p className="text-2xl md:text-3xl font-bold text-purple-600">{stats.totalEntries}</p>
              </div>
              <div className="w-10 h-10 md:w-14 md:h-14 bg-purple-100 rounded-xl flex items-center justify-center">
                <img src={Icons.taskDaily} alt="Total" className="w-5 h-5 md:w-7 md:h-7" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="m-2 md:m-10 mt-3 md:mt-8 p-4 md:p-10 bg-white rounded-3xl dark:bg-secondary-dark-bg min-h-screen">
        {/* Filters and Search */}
        <div className="flex flex-col gap-4 mb-6 md:mb-8">
          <div className="flex flex-wrap justify-between items-start gap-3">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-slate-800 mb-0.5">Clock In/Out Records</h2>
              <p className="text-sm text-slate-500">Monitor student entry and exit times</p>
            </div>
            <button
              onClick={handleExport}
              className="inline-flex items-center px-3 md:px-4 py-2 md:py-2.5 bg-indigo-800 hover:bg-indigo-700 text-white font-medium rounded-full transition-colors shadow-sm flex-shrink-0"
            >
              <FiDownload className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Export Report</span>
            </button>
          </div>

          {/* Filter Row */}
          <div className="flex flex-col md:flex-row gap-3">
            {/* Search */}
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MdSearch className="h-5 w-5 text-slate-400" />
              </div>
              <input
                type="text"
                placeholder="Search by student name..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Date range row on mobile */}
            <div className="grid grid-cols-2 md:contents gap-3">
              {/* Date From */}
              <div className="relative">
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full px-3 md:px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>

              {/* Date To */}
              <div className="relative">
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full px-3 md:px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>

            {/* Class + Status filters row on mobile */}
            <div className="grid grid-cols-2 md:contents gap-3">
            {/* Class Filter */}
            <div className="relative">
              <button
                onClick={() => setShowClassDropdown(!showClassDropdown)}
                className="w-full px-3 md:px-4 py-2.5 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors flex items-center gap-2 md:min-w-[150px]"
              >
                <span className="text-sm">{getSelectedClassName()}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showClassDropdown && (
                <div className="absolute top-full mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-lg z-10 max-h-60 overflow-y-auto">
                  <button
                    onClick={() => {
                      setSelectedClass('');
                      setShowClassDropdown(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm"
                  >
                    All Classes
                  </button>
                  {classes.map((cls) => (
                    <button
                      key={cls._id}
                      onClick={() => {
                        setSelectedClass(cls._id);
                        setShowClassDropdown(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm"
                    >
                      {cls.class_name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Status Filter */}
            <div className="relative">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className="w-full px-3 md:px-4 py-2.5 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors flex items-center gap-2 md:min-w-[140px]"
              >
                <span className="text-sm">{getSelectedStatusLabel()}</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showStatusDropdown && (
                <div className="absolute top-full mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-lg z-10">
                  <button
                    onClick={() => {
                      setSelectedStatus('all');
                      setShowStatusDropdown(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm"
                  >
                    All Status
                  </button>
                  <button
                    onClick={() => {
                      setSelectedStatus('clocked_in');
                      setShowStatusDropdown(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm"
                  >
                    In School
                  </button>
                  <button
                    onClick={() => {
                      setSelectedStatus('clocked_out');
                      setShowStatusDropdown(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-slate-50 text-sm"
                  >
                    Left School
                  </button>
                </div>
              )}
            </div>
            </div>{/* end grid grid-cols-2 */}
          </div>
        </div>

        {/* Data Table */}
        <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
          <DataTable
            columns={columns}
            data={filteredRecords}
            pagination
            paginationComponent={CustomPagination}
            paginationRowsPerPageOptions={[10, 20, 30, 50]}
            customStyles={customStyles}
            progressPending={loading}
            progressComponent={<SkeletonTable rows={5} columns={6} />}
            highlightOnHover
            striped
            noDataComponent={
              <div className="text-center py-16">
                <div className="mx-auto h-24 w-24 text-slate-300 mb-6 flex items-center justify-center">
                  <FiAlertCircle className="w-16 h-16" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No records found</h3>
                <p className="text-slate-500 text-sm">
                  {searchText
                    ? 'No students match your search criteria'
                    : 'No clock-in/out records for the selected date range'}
                </p>
              </div>
            }
          />
        </div>
      </div>

      {/* Student Clock In/Out History Modal */}
      {showStudentModal && selectedStudent && termStartDate && termEndDate && (
        <StudentClockInHistoryModal
          isOpen={showStudentModal}
          onClose={handleCloseStudentModal}
          student={selectedStudent}
          schoolId={schoolId}
          termStartDate={termStartDate}
          termEndDate={termEndDate}
        />
      )}

      {/* Clock In/Out Settings Modal */}
      {showSettingsModal && (
        <ClockInOutSettingsModal
          isOpen={showSettingsModal}
          onClose={() => {
            setShowSettingsModal(false);
          }}
          schoolId={schoolId}
          refreshSchool={refreshSchool}
        />
      )}
    </>
  );
};

export default ClockInOutHistory;

